import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { LuArrowRight } from 'react-icons/lu'
import SavingsCalculator from '../components/SavingsCalculator.jsx'
import { COMPETITORS, HIGH_STREET, KEYZEE_ROW, VERIFIED_AT_LABEL } from '../data/competitors.js'
import './HousePrices.css'
import './Compare.css'

const SITE = 'https://keyzee.co.uk'

/**
 * /compare-estate-agents/ — the hub for the per-competitor "<brand> alternative"
 * pages. Gives the category query ("online estate agents compared") a single
 * target and funnels internal links down to each comparison page.
 *
 * Prerendered by scripts/prerender.mjs (route listed in COMPARE_ROUTES).
 */
const CompareHubPage = () => {
  const canonical = `${SITE}/compare-estate-agents/`
  const title = 'Online Estate Agents Compared 2026: Fees, Contracts and Free Options | Keyzee'
  const metaDescription =
    'Compare online estate agents in 2026 — OffAgent, Purplebricks, Yopa and 99home — on fees, commission and contracts, against selling free on Keyzee with 0% commission.'

  const rows = [KEYZEE_ROW, ...COMPETITORS, HIGH_STREET]

  const faqs = [
    {
      q: 'Which online estate agent is cheapest?',
      a: `Keyzee is free — £0 to list and £0 commission on sale. Among paid platforms, entry pricing starts around £99 (OffAgent, 99home), while fixed-fee hybrid agents such as Yopa and Purplebricks run to four figures. Pricing checked ${VERIFIED_AT_LABEL}.`,
    },
    {
      q: 'Can I sell my house without paying any fee at all?',
      a: 'Yes. Private sales are entirely legal in the UK and you are not required to instruct an estate agent. You will still need a conveyancer for the legal work, and you will need to handle viewings and buyer questions yourself.',
    },
    {
      q: 'What is the difference between a fixed fee and a subscription?',
      a: 'A fixed fee is one payment regardless of how long the sale takes, but is usually owed even if you never sell. A subscription is cheaper to start but bills every month your home is on the market, so a slow sale costs considerably more than a fast one.',
    },
    {
      q: 'Do cheaper platforms still list on Rightmove?',
      a: 'Most do, because portal exposure is the main thing sellers are buying. Keyzee lists on Keyzee and is designed to run alongside a Rightmove listing if you have one — see our guide to listing in both places.',
    },
  ]

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Compare estate agents', item: canonical },
    ],
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Online estate agents compared 2026 | Keyzee" />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${SITE}/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Online estate agents compared 2026 | Keyzee" />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={`${SITE}/og-image.jpg`} />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <section className="region-hero">
        <p className="search-hero__eyebrow">Compare</p>
        <h1>Online estate agents compared</h1>
        <p className="region-hero__lede">
          Every platform here will get your home in front of buyers. They differ on what that costs, when you
          pay, and what happens if the sale takes longer than you hoped. Here is the honest version —
          including where the paid options beat us.
        </p>
        <div className="region-cta__actions">
          <Link className="primary-button pill-action" to="/sell/new">
            List your home free <LuArrowRight aria-hidden="true" />
          </Link>
          <Link className="secondary-button pill-action" to="/sell-without-an-agent/">
            Read the selling guide
          </Link>
        </div>
      </section>

      <section className="compare-table-wrap" aria-label="Estate agent platforms compared">
        <h2>Fees and contracts at a glance</h2>
        <div className="compare-table-scroll">
          <table className="compare-table compare-table--wide">
            <thead>
              <tr>
                <th scope="col">Platform</th>
                <th scope="col">Headline cost</th>
                <th scope="col">Commission</th>
                <th scope="col">Contract</th>
                <th scope="col">When you pay</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name} className={row.name === 'Keyzee' ? 'is-keyzee' : undefined}>
                  <th scope="row">
                    {row.slug ? <Link to={`/${row.slug}-alternative/`}>{row.name}</Link> : row.name}
                  </th>
                  <td>{row.headlineFee}</td>
                  <td>{row.commission}</td>
                  <td>{row.contract}</td>
                  <td>{row.payWhen}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="compare-verified">
          Competitor pricing checked on {VERIFIED_AT_LABEL} against published rates. Prices and packages
          change — confirm on the provider's own site before deciding.
        </p>
      </section>

      <SavingsCalculator initialPrice={250000} />

      <section className="region-section" aria-label="Individual comparisons">
        <h2>Compare Keyzee with each platform</h2>
        <ul className="region-index-list">
          {COMPETITORS.map((c) => (
            <li key={c.slug}>
              <Link to={`/${c.slug}-alternative/`}>
                <span className="region-index-name">Keyzee vs {c.name}</span>
                <span className="region-index-meta">{c.headlineFee}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <section className="region-section" aria-label="How to choose">
        <h2>How to actually choose</h2>
        <ul className="sell-guide-checks">
          <li>
            <span>
              <strong>If you want it all handled, pay for it.</strong> A high-street or hybrid agent will run
              valuations, viewings and negotiation. That is a real service and it costs real money.
            </span>
          </li>
          <li>
            <span>
              <strong>If you are paying mainly for portal exposure, pay as little as possible.</strong> Once
              you have decided to do the work yourself, the only question is how much distribution costs — and
              a subscription that renews monthly is the option most exposed to a slow market.
            </span>
          </li>
          <li>
            <span>
              <strong>If you are happy talking to buyers, pay nothing.</strong> Keyzee is free, has no
              contract, and can run alongside a Rightmove listing.{' '}
              <Link to="/also-list/">See how to list in both places →</Link>
            </span>
          </li>
        </ul>
      </section>

      <section className="sell-guide-faq">
        <div className="sell-guide-shell">
          <h2>Common questions</h2>
          <div className="sell-guide-faq__grid">
            {faqs.map((item) => (
              <article key={item.q} className="sell-guide-faq__item">
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

export default CompareHubPage
